import React, { useRef, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Loader2, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@cms/ui/components/dialog";
import { Button } from "@cms/ui/components/button";
import { Label } from "@cms/ui/components/label";
import { ColorPicker, type ColorPreset } from "@cms/ui/components/color-picker";
import {
  newTimelineStep,
  type SectionTimeline,
  type TimelineLayout,
  type TimelineStep,
} from "../lib/editor-content";
import { ImageField } from "./image-field";
import { Segmented } from "@cms/ui/components/segmented";
import { ColumnBlocksEditor, type ColumnBlocksEditorHandle } from "./column-blocks-editor";
import { useMediaPicker } from "./media-picker-dialog";
import type { ApiClient } from "../lib/api";

type BlockData = Record<string, any>;

/**
 * Modal editor for a timeline section: the layout choice, marker/line styling
 * (numbers or dots, marker + line colours), optional content above/below the
 * steps, and the ordered steps. Each step is one block editor (like a section
 * column) plus an optional marker image; steps reorder by drag or the arrow
 * buttons. Block content lives in the (vanilla) Editor.js instances and is
 * pulled on Done via each editor's imperative getBlocks() — never on keystroke.
 * Edits are local until Done; Cancel discards. Empty steps are kept while
 * editing but never render on the site (the renderer skips them).
 */
export function TimelineEditorDialog({
  section,
  api,
  activeModules,
  palette,
  presets,
  eventsEnabled,
  bookingEnabled,
  menuEnabled,
  onSave,
  onCancel,
}: {
  section: { timeline?: SectionTimeline };
  api: ApiClient;
  /** Active module slugs — module pages join the button block's link picker. */
  activeModules?: string[];
  /** Tenant palette colours for the button block's colour picker. */
  palette?: string[];
  /** Tenant palette presets for the marker/line colour pickers. */
  presets?: ColorPreset[];
  /** Offer the events listing block when the module is active. */
  eventsEnabled?: boolean;
  bookingEnabled?: boolean;
  menuEnabled?: boolean;
  onSave: (timeline: SectionTimeline) => void;
  onCancel: () => void;
}) {
  const [timeline, setTimeline] = useState<SectionTimeline>(() =>
    JSON.parse(
      JSON.stringify(
        section.timeline ?? { layout: "vertical", steps: [], showNumbers: true },
      ),
    ),
  );
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAbove, setShowAbove] = useState(() => (timeline.above?.length ?? 0) > 0);
  const [showBelow, setShowBelow] = useState(() => (timeline.below?.length ?? 0) > 0);

  const { pickImage, pickerElement } = useMediaPicker(api);

  // Live block editors are read on demand: one per step (keyed by step id) plus
  // the optional above/below editors.
  const stepEditorsRef = useRef(new Map<string, ColumnBlocksEditorHandle | null>());
  const aboveEditorRef = useRef<ColumnBlocksEditorHandle | null>(null);
  const belowEditorRef = useRef<ColumnBlocksEditorHandle | null>(null);

  const patch = (next: Partial<SectionTimeline>) => setTimeline((prev) => ({ ...prev, ...next }));

  const setStep = (index: number, stepPatch: Partial<TimelineStep>) => {
    setTimeline((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) => (i === index ? { ...step, ...stepPatch } : step)),
    }));
  };

  const moveStep = (index: number, delta: number) => {
    setTimeline((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      const [moved] = steps.splice(index, 1);
      steps.splice(target, 0, moved);
      return { ...prev, steps };
    });
  };

  const removeStep = (index: number) => {
    const removed = timeline.steps[index];
    if (removed) stepEditorsRef.current.delete(removed.id);
    setTimeline((prev) => ({ ...prev, steps: prev.steps.filter((_, i) => i !== index) }));
  };

  const addStep = () => {
    setTimeline((prev) => ({ ...prev, steps: [...prev.steps, newTimelineStep()] }));
  };

  // Native HTML5 drag-and-drop: the list reorders live as the dragged row
  // passes over its siblings; the arrow buttons stay as the keyboard path.
  // Editors are keyed by step id, so each mounted instance moves with its row
  // without losing its live content.
  const onDragOverRow = (event: React.DragEvent, index: number) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    moveStep(dragIndex, index - dragIndex);
    setDragIndex(index);
  };

  const finish = async () => {
    setBusy(true);
    try {
      const steps: TimelineStep[] = await Promise.all(
        timeline.steps.map(async (step) => {
          const handle = stepEditorsRef.current.get(step.id);
          const blocks = handle ? await handle.getBlocks() : step.blocks;
          return { ...step, blocks };
        }),
      );
      const above =
        showAbove && aboveEditorRef.current ? await aboveEditorRef.current.getBlocks() : [];
      const below =
        showBelow && belowEditorRef.current ? await belowEditorRef.current.getBlocks() : [];
      const next: SectionTimeline = { ...timeline, steps };
      if (above.length) next.above = above;
      else delete next.above;
      if (below.length) next.below = below;
      else delete next.below;
      onSave(next);
    } finally {
      setBusy(false);
    }
  };

  const blockEditor = (
    blocks: BlockData[],
    assign: (handle: ColumnBlocksEditorHandle | null) => void,
    placeholder: string,
  ) => (
    <ColumnBlocksEditor
      ref={assign}
      blocks={blocks}
      api={api}
      placeholder={placeholder}
      pickImage={pickImage}
      eventsEnabled={eventsEnabled}
      bookingEnabled={bookingEnabled}
      menuEnabled={menuEnabled}
      activeModules={activeModules}
      palette={palette}
    />
  );

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onCancel(); }}>
      {/* aria-describedby: the dialog has no description text (Radix warns otherwise). */}
      <DialogContent
        hideClose
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        aria-describedby={undefined}
      >
        <div className="flex items-center justify-between gap-2">
          <DialogTitle>Edit timeline</DialogTitle>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => { void finish(); }} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Done
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Layout</Label>
          <Segmented<TimelineLayout>
            value={timeline.layout}
            options={[
              { value: "vertical", label: "Vertical" },
              { value: "horizontal", label: "Cards" },
              { value: "zigzag", label: "Zig-zag" },
            ]}
            onChange={(layout) => patch({ layout })}
          />
          <p className="text-xs text-muted-foreground">
            Cards and zig-zag layouts stack vertically on small screens.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Markers</Label>
          <Segmented<"numbers" | "dots">
            value={timeline.showNumbers ? "numbers" : "dots"}
            options={[
              { value: "numbers", label: "Numbers" },
              { value: "dots", label: "Dots" },
            ]}
            onChange={(value) => patch({ showNumbers: value === "numbers" })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Marker colour</Label>
            <ColorPicker
              value={timeline.markerColor ?? ""}
              onChange={(value) => patch({ markerColor: value || undefined })}
              presets={presets}
              ariaLabel="Marker colour"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Line colour</Label>
            <ColorPicker
              value={timeline.lineColor ?? ""}
              onChange={(value) => patch({ lineColor: value || undefined })}
              presets={presets}
              ariaLabel="Line colour"
            />
          </div>
        </div>

        {/* Optional content above the steps. */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Content above</Label>
            {showAbove && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => { aboveEditorRef.current = null; setShowAbove(false); patch({ above: undefined }); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
          {showAbove ? (
            blockEditor(timeline.above ?? [], (h) => { aboveEditorRef.current = h; }, "Intro content…")
          ) : (
            <Button type="button" variant="outline" className="w-full border-dashed" onClick={() => setShowAbove(true)}>
              <Plus className="h-4 w-4" />
              Add content above
            </Button>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Steps</Label>
          {timeline.steps.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No steps yet — add your first step below.
            </p>
          )}
          <ul className="space-y-2">
            {timeline.steps.map((step, index) => (
              <li
                key={step.id}
                draggable
                onDragStart={(e: React.DragEvent) => {
                  setDragIndex(index);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e: React.DragEvent) => onDragOverRow(e, index)}
                onDragEnd={() => setDragIndex(null)}
                className={`rounded-md border bg-background ${dragIndex === index ? "opacity-60" : ""}`}
              >
                <div className="flex items-center gap-2 border-b px-3 py-2">
                  <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />
                  <span className="flex-1 text-sm font-medium">Step {index + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index === 0}
                    aria-label="Move step up"
                    onClick={() => moveStep(index, -1)}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index === timeline.steps.length - 1}
                    aria-label="Move step down"
                    onClick={() => moveStep(index, 1)}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    aria-label="Remove step"
                    onClick={() => removeStep(index)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="space-y-3 p-3">
                  <div className="space-y-1.5">
                    <Label>Content</Label>
                    {blockEditor(
                      step.blocks,
                      (h) => {
                        if (h) stepEditorsRef.current.set(step.id, h);
                        else stepEditorsRef.current.delete(step.id);
                      },
                      "What happens in this step?",
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Icon or image (optional)</Label>
                    <ImageField
                      api={api}
                      value={step.imageKey ? { key: step.imageKey, url: step.imageUrl ?? null } : null}
                      onChange={(item) =>
                        setStep(index, item
                          ? { imageKey: item.key, imageUrl: item.url ?? undefined }
                          : { imageKey: "", imageUrl: undefined })
                      }
                      emptyLabel={timeline.showNumbers ? "Numbered marker" : "Dot marker"}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <Button type="button" variant="outline" className="w-full border-dashed" onClick={addStep}>
            <Plus className="h-4 w-4" />
            Add step
          </Button>
        </div>

        {/* Optional content below the steps. */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Content below</Label>
            {showBelow && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => { belowEditorRef.current = null; setShowBelow(false); patch({ below: undefined }); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
          {showBelow ? (
            blockEditor(timeline.below ?? [], (h) => { belowEditorRef.current = h; }, "Closing content…")
          ) : (
            <Button type="button" variant="outline" className="w-full border-dashed" onClick={() => setShowBelow(true)}>
              <Plus className="h-4 w-4" />
              Add content below
            </Button>
          )}
        </div>

        {pickerElement}
      </DialogContent>
    </Dialog>
  );
}
